import React from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useGetCaregiverScheduleQuery } from '../../../../redux/bulkAssignShiftsApi';
import OverflowTooltip from '../../../OverflowTooltip/OverflowTooltip';
import SearchIcon from '../../../../assets/images/search-professional.svg';
import SpinnerIcon from '../../../../assets/images/loader.svg';
import refreshIcon from '../../../../assets/images/refresh-purple.svg';
import calendarCheckIcon from '../../../../assets/images/calendar-check.svg';
import unavailableIcon from '../../../../assets/images/beach-access.svg';

const EMPTY_MESSAGE = 'Select a professional to see their upcoming schedule here.';
const NO_SHIFTS_MESSAGE = 'There are no upcoming shifts for this professional.';
const HIDDEN_FACILITY_LABEL = 'Facility hidden';

const formatShiftTimeRange = (startTime, endTime, facilityTimezone) =>
	`${moment.tz(startTime, facilityTimezone).format('h:mm A')}-${moment.tz(endTime, facilityTimezone).format('h:mm A')}`;

const formatDateLine = (date) => (
	<>
		{date.format('ddd')} <strong>{date.format('MM/DD')}</strong>
	</>
);

const ShiftCard = ({ entry }) => {
	const resourceLabel = entry.specialty ? `${entry.resource_type} - ${entry.specialty}` : entry.resource_type;
	const shiftDate = moment.tz(entry.start_time, entry.timezone);
	const facilityLabel = entry.user_has_portal_access ? entry.facility_name : HIDDEN_FACILITY_LABEL;

	return (
		<div className="schedule-card schedule-card--shift">
			{entry.user_has_portal_access ? (
				<OverflowTooltip className="schedule-card__facility" tooltipClassName="assign-schedule-facility-tooltip">
					{facilityLabel}
				</OverflowTooltip>
			) : (
				<div className="schedule-card__facility schedule-card__facility--hidden">{facilityLabel}</div>
			)}
			<div className="schedule-card__resource">{resourceLabel}</div>
			<div className="schedule-card__row">
				<div className="schedule-card__time">
					{formatShiftTimeRange(entry.start_time, entry.end_time, entry.timezone)}
				</div>
				<div className="schedule-card__date">{formatDateLine(shiftDate)}</div>
			</div>
		</div>
	);
};

const UnavailabilityCard = ({ entry }) => {
	return (
		<div className="schedule-card schedule-card--unavailability">
			<div className="schedule-card__row">
				<div className="schedule-card__title">
					<div className="schedule-card__title-icon">
						<img src={unavailableIcon} alt="" />
					</div>
					Unavailable
				</div>
				<div className="schedule-card__date">{formatDateLine(moment(entry.date))}</div>
			</div>
		</div>
	);
};

const BulkAssignShiftsSchedulePane = ({ facilityId, caregiverId }) => {
	const skip = !facilityId || !caregiverId;
	const { data, isFetching, isError, refetch } = useGetCaregiverScheduleQuery(
		{ facilityId, caregiverId },
		{ skip, refetchOnMountOrArgChange: true }
	);

	if (skip) {
		return (
			<>
				<div className="section-header schedule-panel__header">
					<span>Professional&apos;s schedule</span>
				</div>
				<div className="horizontal-ruler" />
				<div className="schedule-panel empty">
					<img src={SearchIcon} alt="search" />
					<div>{EMPTY_MESSAGE}</div>
				</div>
			</>
		);
	}

	const entries = data || [];

	return (
		<>
			<div className="section-header schedule-panel__header">
				<span>Professional&apos;s schedule</span>
				<button
					type="button"
					className="schedule-panel__refresh"
					onClick={refetch}
					disabled={isFetching}
					aria-label="Refresh schedule"
				>
					<img src={refreshIcon} alt="" />
				</button>
			</div>
			<div className="horizontal-ruler" />
			<div className={classNames('schedule-panel', { empty: isFetching || !entries?.length })}>
				{isFetching && (
					<div className="schedule-panel__status">
						<img src={SpinnerIcon} width="32" height="32" alt="" />
						<div>Loading upcoming shifts...</div>
					</div>
				)}
				{!isFetching && isError && (
					<div className="schedule-panel__status schedule-panel__status--error">
						Could not load schedule. Please try again.
					</div>
				)}
				{!isFetching && !isError && entries.length === 0 && (
					<>
						<img src={calendarCheckIcon} alt="" />
						<div className="schedule-panel__no-shifts">{NO_SHIFTS_MESSAGE}</div>
					</>
				)}
				{!isFetching &&
					!isError &&
					entries.map((entry) =>
						entry.type === 'shift' ? (
							<ShiftCard key={`shift-${entry.start_time}-${entry.end_time}`} entry={entry} />
						) : (
							<UnavailabilityCard key={`unavailability-${entry.date}`} entry={entry} />
						)
					)}
			</div>
		</>
	);
};

export default BulkAssignShiftsSchedulePane;
